import {
  Box,
  Paper,
  Stack,
  Typography,
  Chip,
  Button,
  Divider,
  Grid,
} from '@mui/material';
import { useOrg } from '../../organization/useOrg';
import { useState, useEffect } from 'react';
import { useGetProject } from '@/queries/useProjects';
import { useFiscalYears } from '@/fiscalYears/useFiscalYears';
import { Link, useParams } from 'react-router-dom';
import { Routes } from '@/CSIRouter';
import { ManagerialBlock } from '@/tools/ManagerialComponents';
import type { Department } from '@/types/department';
import type { Member } from '@/types/user';
import { useQuery } from '@tanstack/react-query';
import { fetchOrganizationUsers } from '@/services/userService';
import { useDepartmentService } from '@/api/services/departmentService';
import { ErrorOutline } from '@mui/icons-material';

export function ReadProjOverview() {
  const { selectedOrg } = useOrg();
  const { projectId } = useParams();
  const departmentService = useDepartmentService();
  const { data: project } = useGetProject(selectedOrg?.id, projectId);
  const { data: fiscalYears } = useFiscalYears(selectedOrg?.id || '');
  const [projTitle, setName] = useState('');
  const [description, setDescription] = useState('');
  const [address, setAddress] = useState('');
  const [newFisc, setNewFisc] = useState('');
  const [projectMaint, setProjectMain] = useState('');
  const [projDesign, setProjDesign] = useState('');
  const [userProjectId, setUserProjId] = useState('');
  const [sponsoringDeptIds, setSponsoringDeptIds] = useState<string[]>([]);
  const [overseeingDeptIds, setOverseeingDeptIds] = useState<string[]>([]);
  const [usingDeptIds, setUsingDeptIds] = useState<string[]>([]);
  const [progManagerIds, setProgManagerIds] = useState<string[]>([]);
  const [projManagerIds, setProjManagerIds] = useState<string[]>([]);
  const [budgetOfficerIds, setBudgetOfficerIds] = useState<string[]>([]);
  const RESPONSIVE_TEXT_FONT = 'clamp(10px, 0.9vw, 12px)';

  // get department / user options for managerial page
  const departmentsQuery = useQuery({
    queryKey: ['departments', selectedOrg?.id],
    queryFn: async () => {
      const response = await departmentService.list();
      return response.data; // Extract data from ApiResponse
    },
    enabled: !!selectedOrg,
  });

  const usersQuery = useQuery({
    queryKey: [
      'organization-users',
      selectedOrg?.id,
      1,
      '', // search
      false, // showArchived
      '', // department
    ],
    queryFn: () =>
      fetchOrganizationUsers({
        organizationId: selectedOrg?.id || '',
        page: 1,
        search: '',
        showArchived: false,
        department: '',
      }),
    enabled: !!selectedOrg,
  });

  const availableDepartments: Department[] = departmentsQuery.data || [];
  const availableMembers: Member[] = usersQuery.data?.results || [];

  useEffect(() => {
    if (fiscalYears) {
      const curFiscalYear = fiscalYears.find(
        (fy) => fy.id === project?.fiscalYear,
      );
      setNewFisc(curFiscalYear?.name.replace('FY ', '') || '');
    }
  }, [fiscalYears, project?.fiscalYear]);

  useEffect(() => {
    if (project) {
      const curFiscalYear = fiscalYears?.find(
        (fy) => fy.id === project.fiscalYear,
      );
      setNewFisc(curFiscalYear?.name.replace('FY ', '') || '');
      setName(project.name || '');
      setDescription(project.description || '');
      setAddress(project.address || '');
      setProjectMain(project.projectMajorMaintenanceYears?.toString() || '');
      setProjDesign(project.projectLifetimeYears?.toString() || '');
      setUserProjId(project.userProjectId || '');
      setSponsoringDeptIds(project.sponsoringDepts || []);
      setOverseeingDeptIds(project.overseeingDepts || []);
      setUsingDeptIds(project.usingDepts || []);
      setProgManagerIds(project.programManagers || []);
      setProjManagerIds(project.projectManagers || []);
      setBudgetOfficerIds(project.budgetOfficers || []);
    }
  }, [project, fiscalYears]);

  return (
    <>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          mb: 2,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant='h6' sx={{ color: 'white', fontWeight: 700 }}>
            Project Details
          </Typography>
          {project?.fiscalYearDetails?.name && (
            <Chip
              label={project.fiscalYearDetails.name}
              size='small'
              sx={{
                bgcolor: 'rgba(255,255,255,0.15)',
                color: 'white',
                fontWeight: 600,
              }}
            />
          )}
        </Box>
        {
          <Button
            size='small'
            variant='outlined'
            component={Link}
            to={'../' + Routes.Project.ProjectOverview.path}
            sx={{
              color: 'white',
              borderColor: 'rgba(255,255,255,0.5)',
              textDecoration: 'none',
            }}
          >
            Edit
          </Button>
        }
      </Box>
      <Box
        sx={{ display: 'flex', p: 3, mb: 5, justifyContent: 'space-between' }}
      >
        <Box>
          <Typography variant='h5' fontWeight='bold'>
            {projTitle}
          </Typography>
          <Typography variant='body2'>{address}</Typography>
          {userProjectId && (
            <Typography variant='body2'>Project ID: {userProjectId}</Typography>
          )}
        </Box>

        <Typography variant='subtitle1' fontWeight='bold'>
          {newFisc}
        </Typography>
      </Box>

      <Paper sx={{ p: 3, mb: 5, borderRadius: 3 }}>
        {description ? (
          <>
            <Box
              sx={{
                pl: '1vh',
                borderRadius: 2,
              }}
            >
              <Typography variant='subtitle1' fontWeight='bold'>
                Description
              </Typography>
            </Box>

            <Typography variant='body2' sx={{ pl: 2 }}>
              {description}
            </Typography>
          </>
        ) : (
          <>
            <Box
              sx={{
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'flex-start',
                gap: 2,
                pb: 5,
                pt: 5,
              }}
            >
              <ErrorOutline
                sx={{ fontSize: 25, color: 'rgba(255,255,255,0.5)' }}
              />
              <Box sx={{ textAlign: 'center' }}>
                <Typography
                  variant='caption'
                  sx={{
                    display: 'block',
                    color: 'white',
                    fontWeight: 700,
                    letterSpacing: 1,
                    textTransform: 'uppercase',
                  }}
                >
                  Continue with general overview to add project details
                </Typography>
              </Box>
            </Box>
          </>
        )}
      </Paper>
      <Stack
        direction='row'
        divider={
          projectMaint !== '0' ? (
            <Divider orientation='vertical' flexItem />
          ) : (
            <></>
          )
        }
        spacing={2}
        justifyContent='space-around'
        sx={{ p: 3, mb: 5 }}
      >
        {projDesign !== '0' && (
          <Box textAlign='center'>
            <Typography variant='subtitle2'>Project Lifetime</Typography>
            <Typography variant='h6'>{projDesign} years</Typography>
          </Box>
        )}

        {projectMaint !== '0' && (
          <Box textAlign='center'>
            <Typography variant='subtitle2'>Major Maintenance Cycle</Typography>
            <Typography variant='h6'>{projectMaint} years</Typography>
          </Box>
        )}
      </Stack>
      <Paper sx={{ p: 3, mb: 5, borderRadius: 3 }}>
        {/* MANAGERIAL SECTION */}
        {progManagerIds.length > 0 ||
        projManagerIds.length > 0 ||
        budgetOfficerIds.length > 0 ||
        sponsoringDeptIds.length > 0 ||
        overseeingDeptIds.length > 0 ||
        usingDeptIds.length > 0 ? (
          <>
            <Box
              sx={{
                pl: '1vh',
                borderRadius: 2,
                mb: 2,
              }}
            >
              <Typography variant='subtitle1' fontWeight='bold'>
                Managerial Summary
              </Typography>
            </Box>
            <Grid
              container
              spacing={'2rem'}
              sx={{ display: 'flex', gap: 4, pl: 10, pt: 8 }}
            >
              <ManagerialBlock
                title={'Program Manager(s)'}
                selectedIds={progManagerIds}
                dropdownOptions={availableMembers}
                fontSize={RESPONSIVE_TEXT_FONT}
                isSubprojectPage={false}
              />
              <ManagerialBlock
                title={'Budget Officer(s)'}
                selectedIds={budgetOfficerIds}
                dropdownOptions={availableMembers}
                fontSize={RESPONSIVE_TEXT_FONT}
                isSubprojectPage={false}
              />
              <ManagerialBlock
                title={'Project Manager(s)'}
                selectedIds={projManagerIds}
                dropdownOptions={availableMembers}
                fontSize={RESPONSIVE_TEXT_FONT}
                isSubprojectPage={false}
              />
              <ManagerialBlock
                title={'Sponsoring Department(s)'}
                selectedIds={sponsoringDeptIds}
                dropdownOptions={availableDepartments}
                fontSize={RESPONSIVE_TEXT_FONT}
                isSubprojectPage={false}
              />
              <ManagerialBlock
                title={'Overseeing Department(s)'}
                selectedIds={overseeingDeptIds}
                dropdownOptions={availableDepartments}
                fontSize={RESPONSIVE_TEXT_FONT}
                isSubprojectPage={false}
              />
              <ManagerialBlock
                title={'Using Department(s)'}
                selectedIds={usingDeptIds}
                dropdownOptions={availableDepartments}
                fontSize={RESPONSIVE_TEXT_FONT}
                isSubprojectPage={false}
              />
            </Grid>
          </>
        ) : (
          <>
            <Box
              sx={{
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'flex-start',
                gap: 2,
                pb: 5,
                pt: 5,
              }}
            >
              <ErrorOutline
                sx={{ fontSize: 25, color: 'rgba(255,255,255,0.5)' }}
              />
              <Box sx={{ textAlign: 'center' }}>
                <Typography
                  variant='caption'
                  sx={{
                    display: 'block',
                    color: 'white',
                    fontWeight: 700,
                    letterSpacing: 1,
                    textTransform: 'uppercase',
                  }}
                >
                  Finalize project details to add roles
                </Typography>
              </Box>
            </Box>
          </>
        )}
      </Paper>
    </>
  );
}
